import { Button } from '@heidi/design-system/button';
import { Skeleton } from '@heidi/design-system/skeleton';
import { TypographyCaptionBold } from '@heidi/design-system/typography';
import { type DetailedPublicTemplateResponse } from '@heidi/templates/contracts';
import { cn } from '@heidi/utils';
import { Download, Expand } from 'lucide-react';
import dynamic from 'next/dynamic';
import type { ComponentProps } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { communityTemplateDetailsMessages } from '../../communityTemplateDetails.messages';
import { TemplateFormDemo } from '../../template-form-demo';
import type {
  FormDemoSubmitSource,
  XmlFormFillResult,
} from '../../template-form-demo/types';

const XmlFormPreview = dynamic(
  () => import('./XmlFormPreview').then((m) => m.XmlFormPreview),
  { ssr: false }
);

interface TemplateXmlFormPreviewSectionProps {
  /** Null for a JSON form, which previews as a field list with no download. */
  xmlSource: string | null;
  fieldValues: Record<string, string> | null;
  isLoading: boolean;
  onDownloadXml?: () => void;
  onOpenFullscreenPreview: () => void;
  onXmlFilled?: (result: XmlFormFillResult) => void;
  onJsonFilled?: (fieldValues: Record<string, string>) => void;
  onResetXmlFill: () => void;
  onFormDemoSubmit: (source: FormDemoSubmitSource) => void;
  onFormDemoTrySampleClick: () => void;
  onSignupCtaClick: () => void;
  template: DetailedPublicTemplateResponse | undefined;
  isAuthenticated: boolean;
  useSpecialtyTranscript: ComponentProps<
    typeof TemplateFormDemo
  >['useSpecialtyTranscript'];
  TermsModal: ComponentProps<typeof TemplateFormDemo>['TermsModal'];
}

export const TemplateXmlFormPreviewSection = ({
  xmlSource,
  fieldValues,
  isLoading,
  onDownloadXml,
  onOpenFullscreenPreview,
  onXmlFilled,
  onJsonFilled,
  onResetXmlFill,
  onFormDemoSubmit,
  onFormDemoTrySampleClick,
  onSignupCtaClick,
  template,
  isAuthenticated,
  useSpecialtyTranscript,
  TermsModal,
}: TemplateXmlFormPreviewSectionProps) => {
  const intl = useIntl();
  const fullscreenLabel = intl.formatMessage(
    communityTemplateDetailsMessages.openFormPreviewFullscreen
  );
  const downloadLabel = intl.formatMessage(
    template?.title
      ? communityTemplateDetailsMessages.downloadXmlForTemplate
      : communityTemplateDetailsMessages.downloadXmlButton,
    template?.title ? { title: template.title } : undefined
  );
  const formFields = template?.template_content.form_fields ?? [];

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-4 w-full max-w-md" />
        <Skeleton className="h-150 w-full" />
      </div>
    );
  }

  return (
    <>
      <div className="flex items-start justify-between gap-2">
        <Button
          className="flex items-center gap-2 text-text-secondary"
          variant="ghost"
          size="xs"
          onClick={onOpenFullscreenPreview}
          aria-label={fullscreenLabel}
          title={fullscreenLabel}
        >
          <Expand size={16} />
          <TypographyCaptionBold>
            <FormattedMessage {...communityTemplateDetailsMessages.expandLabel} />
          </TypographyCaptionBold>
        </Button>
        {onDownloadXml && (
          <Button
            disableAutomaticSvgSizing
            variant="ghost"
            size="xs"
            className="flex items-center gap-2 text-text-secondary"
            onClick={onDownloadXml}
            aria-label={downloadLabel}
            title={downloadLabel}
          >
            <Download size={16} />
            <FormattedMessage {...communityTemplateDetailsMessages.downloadXmlButton} />
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-4 @4xl:flex-row @4xl:items-start">
        <div className={cn('size-full @4xl:min-w-0 @4xl:grow @4xl:basis-2/3')}>
          <XmlFormPreview
            xmlSource={xmlSource}
            formFields={formFields}
            fieldValues={fieldValues}
          />
        </div>
        {template && (
          <div className="size-full @4xl:min-w-96 @4xl:max-w-md @4xl:shrink-0 @4xl:basis-1/3">
            <TemplateFormDemo
              template={template}
              useSpecialtyTranscript={useSpecialtyTranscript}
              TermsModal={TermsModal}
              onFilledPdfReady={() => undefined}
              onXmlFilled={onXmlFilled}
              onJsonFilled={onJsonFilled}
              onReset={onResetXmlFill}
              onSubmitAttempt={onFormDemoSubmit}
              onTrySampleClick={onFormDemoTrySampleClick}
              showSignupCta={!isAuthenticated}
              onSignupCtaClick={onSignupCtaClick}
              isUnauthed={!isAuthenticated}
            />
          </div>
        )}
      </div>
    </>
  );
};
